/**
 * Reference cards of one device: REST every 10 s plus Socket.IO `reference_state`.
 * Polling keeps the cards current when the socket is down.
 */

import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';

import { referencesApi } from '../services/referencesApi';
import { tokenStorage } from '../services/httpClient';
import { References } from '../types/references';

export const REFERENCES_POLL_MS = 10_000;

const SOCKET_URL = (process.env.REACT_APP_SOCKET_URL as string) || window.location.origin;

interface ReferenceStateMessage {
  device_id: number;
  references: References;
}

export function useReferences(deviceId: number) {
  const [references, setReferences] = useState<References | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deviceId) return undefined;
    let cancelled = false;
    setReferences(null);

    const load = async () => {
      try {
        const data = await referencesApi.getReferences(deviceId);
        if (!cancelled) {
          setReferences(data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load references');
      }
    };

    load();
    const timer = setInterval(load, REFERENCES_POLL_MS);

    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      path: '/socket.io',
      autoConnect: !!tokenStorage.getAccess(),
      auth: (cb) => cb({ token: tokenStorage.getAccess() || '' }),
    });
    socket.on('reference_state', (msg: ReferenceStateMessage) => {
      if (!cancelled && msg && msg.device_id === deviceId && msg.references) {
        setReferences(msg.references);
      }
    });

    return () => {
      cancelled = true;
      clearInterval(timer);
      socket.disconnect();
    };
  }, [deviceId]);

  return { references, error };
}
